/** @file This file contains the store for assistants. */
import { v4 as uuidv4 } from 'uuid';
import { MessageAggregator } from '@codegrade/assistant-message-aggregator';
import { Maybe } from '@codegrade/maybe';
import { array, eitherAsync, expr, sort, typeHelpers } from '@codegrade/utils';
import { getStoreBuilder } from '@codegrade/vuex-typex';
import { client as apiv2Client } from '@/client-v2';
import * as utils from '@/utils';
import getWsClient from '@/ws_client';
const storeBuilder = getStoreBuilder();
/** @returns A new unique listener ID. */
function newListenerId() {
    return uuidv4();
}
/** @returns The initial state. */
function makeInitialState() {
    return {
        assistants: {},
        conversations: {},
        subscriptions: {},
        messages: {},
        aggregators: {},
        singleAssistantLoaders: {},
        singleConversationLoaders: {},
    };
}
const STATE_KEYS = typeHelpers.keysOfFast([
    'assistants',
    'conversations',
    'subscriptions',
    'messages',
    'aggregators',
    'singleAssistantLoaders',
    'singleConversationLoaders',
]);
const moduleBuilder = storeBuilder.module('assistant', makeInitialState());
/** The store for the assistant. */
export var AssistantsStore;
(function (AssistantsStore) {
    /** Clear the entire store. */
    const commitClearAll = moduleBuilder.commit(state => {
        const initial = makeInitialState();
        for (const key of STATE_KEYS) {
            utils.vueSet(state, key, initial[key]);
        }
    }, 'commitClearAll');
    const commitAssistants = moduleBuilder.commit((state, { assignmentId, newState, }) => {
        utils.vueSet(state.assistants, assignmentId, {
            ...newState,
            data: Array.from(utils.itertools.unique(assistant => assistant.assistantId, sort.sortBy(newState.data ?? [], assistant => [assistant.assistantId]))),
        });
    }, 'commitAssistants');
    const commitSingleAssistantLoader = moduleBuilder.commit((state, { loader, assistantId, }) => {
        if (loader.isJust()) {
            utils.vueSet(state.singleAssistantLoaders, assistantId, loader.value);
        }
        else {
            utils.vueDelete(state.singleAssistantLoaders, assistantId);
        }
    }, 'commitSingleAssistantLoader');
    /** Load assistants of an assignment. */
    AssistantsStore.loadAssistants = moduleBuilder.dispatch(async (ctx, { assignmentId, toLoad }) => {
        await utils.loadPaginatedWithForce({
            getData: () => ctx.state.assistants[assignmentId],
            toLoad,
            load: async (nextToken) => {
                const { ListAssignmentAssistantsCommand } = await import('@codegrade/apiv2-client');
                const resp = await apiv2Client.send(new ListAssignmentAssistantsCommand({
                    assignmentId: assignmentId.toString(10),
                    nextToken,
                }));
                return { data: resp.assistants, nextToken: resp.nextToken };
            },
            commitData: newState => commitAssistants({ assignmentId, newState }),
        });
    }, 'loadAssistants');
    /**
     * Add a single assistant to the store.
     *
     * @param state The current state of the store.
     * @param assignmentId The assignment identifier.
     * @param assistant The assistant identifier.
     */
    function addSingleAssistant(state, assignmentId, assistant) {
        const existingState = state.assistants[assignmentId] ?? {
            data: [],
            pagination: { tag: 'not-started' },
        };
        const existingData = existingState.data ?? [];
        const updatedData = [assistant].concat(existingData);
        const newState = {
            pagination: existingState.pagination,
            data: Object.freeze(updatedData),
        };
        commitAssistants({ assignmentId, newState });
    }
    /**
     * Getter for a single assistant from the store.
     *
     * @param state The current state of the store.
     * @returns The function to retrieve a single assistant given a store state.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getSingleAssistant(state) {
        /**
         * Get a single assistant if it is already loaded in the store.
         *
         * @param param0 The assignment and assistant id used to get the assistant.
         * @returns The requested assistant if it is loaded into the store.
         */
        return ({ assignmentId, assistantId, }) => Maybe.fromNullable(state.assistants[assignmentId].data?.find(assistant => assistant.assistantId === assistantId));
    }
    /** Get a single assistant from the store if it is available. */
    AssistantsStore.getSingleAssistant = moduleBuilder.read(_getSingleAssistant, 'getSingleAssistant');
    /**
     * Load a single assistant from the backend by its id.
     *
     * This is used when we are missing messages in the middle of the conversation.
     */
    AssistantsStore.loadSingleAssistant = moduleBuilder.dispatch(async ({ state }, { assignmentId, assistantId, force = false, }) => {
        const maybeAssistant = _getSingleAssistant(state)({ assignmentId, assistantId });
        /**
         * Requests the single assistant, and commit it to the store.
         */
        const load = async () => {
            const { GetAssistantCommand } = await import('@codegrade/apiv2-client');
            const { assistant } = await apiv2Client.send(new GetAssistantCommand({ assistantId }));
            addSingleAssistant(state, assignmentId, assistant);
        };
        return await utils.storeLoadWithMaybeForce({
            value: maybeAssistant,
            loader: Maybe.fromNullable(state.singleAssistantLoaders[assistantId]),
            force,
            load,
            addLoader: loader => commitSingleAssistantLoader({ loader: Maybe.of(loader), assistantId }),
            removeLoader: () => commitSingleAssistantLoader({ loader: Maybe.empty(), assistantId }),
        });
    }, 'loadSingleAssistant');
    /** Get the assistants in an assignment. */
    AssistantsStore.getAssistants = moduleBuilder.read(state => 
    /**
     * Do the actual getting.
     *
     * @param root0 The data to use.
     * @returns The external state.
     */
    ({ assignmentId }) => utils.getPaginatedState(state.assistants[assignmentId]), 'getAssistants');
    /** Create a new assistant and commit it to the store. */
    AssistantsStore.createAssistant = moduleBuilder.dispatch(async ({ state }, { data, name, systemPrompt }) => {
        const { CreateAssistantCommand } = await import('@codegrade/apiv2-client');
        const dataAssignmentId = data.assignment?.assignmentId;
        if (dataAssignmentId == null) {
            throw Error('No assignmentId found in assistant, this should never happen.');
        }
        const assignmentId = parseInt(dataAssignmentId, 10);
        const { assistant } = await apiv2Client.send(new CreateAssistantCommand({
            data: { assignment: { assignmentId: dataAssignmentId } },
            name,
            systemPrompt,
        }));
        addSingleAssistant(state, assignmentId, assistant);
        return assistant;
    }, 'createAssistant');
    /** Create a new assistant and commit it to the store. */
    AssistantsStore.updateAssistant = moduleBuilder.dispatch(async (ctx, { input, }) => {
        const { UpdateAssistantCommand } = await import('@codegrade/apiv2-client');
        const { assistant } = await apiv2Client.send(new UpdateAssistantCommand(input));
        const dataAssignmentId = assistant.data.assignment?.assignmentId;
        if (dataAssignmentId == null) {
            throw Error('No assignmentId found in assistant, this should never happen.');
        }
        const assignmentId = parseInt(dataAssignmentId, 10);
        const existingState = ctx.state.assistants[assignmentId];
        const existingData = existingState?.data;
        if (existingData == null) {
            throw Error('Could not find the assistant to update.');
        }
        const assistantIndexToUpdate = existingData.findIndex(a => a.assistantId === assistant.assistantId);
        if (assistantIndexToUpdate === -1) {
            throw Error('Could not find the assistant to update.');
        }
        const updatedData = [...existingData];
        updatedData[assistantIndexToUpdate] = assistant;
        const newState = {
            pagination: existingState.pagination,
            data: Object.freeze(updatedData),
        };
        commitAssistants({
            assignmentId,
            newState,
        });
    }, 'updateAssistant');
    /**
     * Get a raw conversation from the store.
     *
     * @param state The current store state.
     * @param ids The ids to get for.
     * @returns The raw state from the store.
     */
    function getConvoState(state, ids) {
        return state.conversations[ids.userId]?.[ids.assignmentId];
    }
    /**
     * Get a raw messages state from the store.
     *
     * @param state The current state of the entire store.
     * @param ids The identifiers to point to a conversation.
     * @returns The state of the messages from the store or undefined if it does not exist yet.
     */
    function getMsgState(state, ids) {
        return state.messages[ids.assistantId]?.[ids.conversationId];
    }
    const removeMessageAggregator = moduleBuilder.commit((state, { assistantId, conversationId, messageId, }) => {
        const byMsgId = state.aggregators[assistantId]?.[conversationId];
        if (byMsgId != null) {
            utils.vueDelete(byMsgId, messageId);
        }
    }, 'removeMessageAggregator');
    const commitMessageAggregator = moduleBuilder.commit((state, { assistantId, conversationId, aggregator, }) => {
        const byConvoId = utils.vueSetDefault(state.aggregators, assistantId, {});
        const byMsgId = utils.vueSetDefault(byConvoId, conversationId, {});
        utils.vueSet(byMsgId, aggregator.messageId, aggregator);
    }, 'commitMessageAggregator');
    const commitMessages = moduleBuilder.commit((state, { assistantId, conversationId, newState, }) => {
        const byConvoId = utils.vueSetDefault(state.messages, assistantId, {});
        const data = newState?.data ?? [];
        utils.vueSet(byConvoId, conversationId, {
            ...newState,
            data: Array.from(utils.itertools.unique(msg => msg.messageId, sort.sortBy(data, 
            // We have to use parseInt here to avoid faulty sorting
            // i.e. ['1', '10', '2']
            message => [parseInt(message.messageId, 10)]))),
        });
    }, 'commitMessages');
    const commitConversations = moduleBuilder.commit((state, { assignmentId, userId, newState, }) => {
        const byUser = utils.vueSetDefault(state.conversations, userId, {});
        const data = newState?.data ?? [];
        utils.vueSet(byUser, assignmentId, {
            ...newState,
            data: Array.from(utils.itertools.unique(convo => `${convo.assistantId}#${convo.conversationId}`, sort.sortBy(data, convo => [convo.createdAt], { reverse: true }))),
        });
    }, 'commitConversations');
    /** Load conversations for a user. */
    AssistantsStore.loadConversations = moduleBuilder.dispatch(async (ctx, { assignmentId, userId, toLoad }) => {
        await utils.loadPaginatedWithForce({
            getData: () => getConvoState(ctx.state, { assignmentId, userId }),
            toLoad,
            load: async (nextToken) => {
                const { ListUserAssistantConversationsCommand } = await import('@codegrade/apiv2-client');
                const resp = await apiv2Client.send(new ListUserAssistantConversationsCommand({
                    assignmentId: assignmentId.toString(10),
                    userId: userId.toString(10),
                    nextToken,
                    pageSize: 20,
                }));
                return { data: resp.conversations, nextToken: resp.nextToken };
            },
            commitData: newState => commitConversations({ assignmentId, userId, newState }),
        });
    }, 'loadConversations');
    /**
     * Getter for a single assistant from the store.
     *
     * @param state The current state of the store.
     * @returns The function to retrieve a single assistant given a store state.
     */
    // eslint-disable-next-line no-underscore-dangle
    function _getSingleConversation(state) {
        /**
         * Get a single assistant if it is already loaded in the store.
         *
         * @param param0 The assignment and assistant id used to get the assistant.
         * @returns The requested assistant if it is loaded into the store.
         */
        return ({ assignmentId, conversationId, userId, assistantId, }) => Maybe.fromNullable(getConvoState(state, { assignmentId, userId })?.data?.find(convo => convo.conversationId === conversationId &&
            convo.assistantId === assistantId));
    }
    /** Get a single assistant from the store if it is available. */
    AssistantsStore.getSingleConversation = moduleBuilder.read(_getSingleConversation, 'getSingleConversation');
    /**
     * Add a single assistant to the store.
     *
     * @param state The current state of the store.
     * @param conversation Conversation to add to the store.
     * @param root0 Identifiers for assignment and user to store under.
     */
    function addSingleConversation(state, conversation, { assignmentId, userId }) {
        const existingState = getConvoState(state, { assignmentId, userId }) ?? {
            pagination: { tag: 'not-started' },
        };
        const existingData = existingState.data ?? [];
        const updatedData = [conversation].concat(existingData);
        const newState = {
            pagination: existingState.pagination,
            data: Object.freeze(updatedData),
        };
        commitConversations({ userId, assignmentId, newState });
    }
    const commitSingleConversationLoader = moduleBuilder.commit((state, { loader, conversationId, }) => {
        if (loader.isJust()) {
            utils.vueSet(state.singleConversationLoaders, conversationId, loader.value);
        }
        else {
            utils.vueDelete(state.singleConversationLoaders, conversationId);
        }
    }, 'commitSingleConversationLoader');
    /**
     * Load a single conversation for a user..
     *
     * Used when are navigating to a specific conversation that falls outside of the first page.
     */
    AssistantsStore.loadSingleConversation = moduleBuilder.dispatch(async ({ state }, { assistantId, conversationId, userId, assignmentId, force = false, }) => {
        const maybeAssistant = _getSingleConversation(state)({
            conversationId,
            assistantId,
            userId,
            assignmentId,
        });
        /**
         * Requests the single assistant, and commit it to the store.
         */
        const load = async () => {
            const { GetAssistantConversationCommand } = await import('@codegrade/apiv2-client');
            const { conversation } = await apiv2Client.send(new GetAssistantConversationCommand({ assistantId, conversationId }));
            addSingleConversation(state, conversation, { assignmentId, userId });
        };
        return await utils.storeLoadWithMaybeForce({
            value: maybeAssistant,
            loader: Maybe.fromNullable(state.singleAssistantLoaders[conversationId]),
            force,
            load,
            addLoader: loader => commitSingleConversationLoader({ loader: Maybe.of(loader), conversationId }),
            removeLoader: () => commitSingleConversationLoader({ loader: Maybe.empty(), conversationId }),
        });
    }, 'loadSingleConversation');
    /** Load messages for a specific conversation. */
    AssistantsStore.loadMessages = moduleBuilder.dispatch(async (ctx, { assistantId, conversationId, toLoad }) => {
        await utils.loadPaginatedWithForce({
            getData: () => getMsgState(ctx.state, { assistantId, conversationId }),
            toLoad,
            load: async (nextToken) => {
                const { ListAssistantMessagesCommand } = await import('@codegrade/apiv2-client');
                const resp = await apiv2Client.send(new ListAssistantMessagesCommand({
                    assistantId,
                    conversationId,
                    pageSize: 20,
                    nextToken,
                }));
                return { data: resp.messages, nextToken: resp.nextToken };
            },
            commitData: newState => commitMessages({ assistantId, conversationId, newState }),
        });
    }, 'loadMessages');
    /** Refreshes messages for a conversation by loading pages until overlap with existing data. */
    AssistantsStore.refreshMessages = moduleBuilder.dispatch(async (ctx, { assistantId, conversationId }) => {
        const { ListAssistantMessagesCommand } = await import('@codegrade/apiv2-client');
        const newState = await utils.loadMoreRecentPages(getMsgState(ctx.state, { assistantId, conversationId }), msg => msg.messageId, async (nextToken) => {
            const resp = await apiv2Client.send(new ListAssistantMessagesCommand({
                assistantId,
                conversationId,
                pageSize: 20,
                nextToken,
            }));
            return { data: resp.messages, nextToken: resp.nextToken };
        });
        commitMessages({ assistantId, conversationId, newState });
    }, 'refreshMessages');
    /** Get the conversations for a user in an assignment. */
    AssistantsStore.getConversations = moduleBuilder.read(state => 
    /**
     * Do the actual getting.
     *
     * @param ids The ids to use.
     * @returns The external state.
     */
    (ids) => utils.getPaginatedState(getConvoState(state, ids)), 'getConversations');
    /**
     * Get the currently generating message if we have one for the conversation.
     *
     * @param aggregators The current aggregators we have.
     * @param assistantId The assistant id.
     * @param conversationId The conversation id.
     * @param lastMsgId The last message id.
     * @returns The generating message if we have one, or `Nothing`.
     */
    function getGeneratingMessage(aggregators, assistantId, conversationId, lastMsgId) {
        const nextMessageId = parseInt(lastMsgId, 10) + 1;
        return Maybe.fromNullable(aggregators[assistantId]?.[conversationId]?.[nextMessageId]?.content);
    }
    /** Get the messages for a conversation. */
    AssistantsStore.getMessages = moduleBuilder.read(state => 
    /**
     * Do the actual getting.
     *
     * @param ids The ids to use.
     * @returns The external state with optional generating message.
     */
    (ids) => {
        const baseState = utils.getPaginatedState(getMsgState(state, ids));
        const lastMsg = array.last(baseState.data ?? []);
        let generatingMessage;
        if (lastMsg == null) {
            generatingMessage = Maybe.empty();
        }
        else {
            generatingMessage = getGeneratingMessage(state.aggregators, ids.assistantId, ids.conversationId, lastMsg.messageId);
        }
        return {
            ...baseState,
            generatingMessage,
        };
    }, 'getMessages');
    /** Create a new conversation. */
    AssistantsStore.createConversation = moduleBuilder.dispatch(async (ctx, { assistant, data, }) => {
        const { CreateAssistantConversationCommand, AssistantData } = await import('@codegrade/apiv2-client');
        const { conversation } = await apiv2Client.send(new CreateAssistantConversationCommand({
            ...data,
            assistantId: assistant.assistantId,
        }));
        const assignmentId = AssistantData.visit(assistant.data, {
            _: () => {
                throw new Error('Only assignment assistants are supported');
            },
            assignment: d => parseInt(d.assignmentId, 10),
        });
        const ids = {
            assignmentId,
            userId: parseInt(conversation.owner, 10),
        };
        const state = getConvoState(ctx.state, ids);
        commitConversations({
            ...ids,
            newState: Object.freeze({
                pagination: Object.freeze({ tag: 'not-started' }),
                ...state,
                data: [conversation].concat(state?.data ?? []),
            }),
        });
        return conversation;
    }, 'createConversation');
    const addSubscription = moduleBuilder.dispatch(({ state }, { assignmentId, userId, data, }) => {
        const byUser = utils.vueSetDefault(state.subscriptions, userId, {});
        utils.vueSet(byUser, assignmentId, data);
    }, 'addSubscription');
    const removeSubscription = moduleBuilder.dispatch(({ state }, { assignmentId, userId }) => {
        const byUser = state.subscriptions[userId];
        if (byUser != null) {
            byUser[assignmentId]?.subscription.then(sub => {
                sub.unsubscribe();
            });
            utils.vueDelete(byUser, assignmentId);
        }
    }, 'removeSubscription');
    /** Remove a listener for a program id, and unsubscribe if needed. */
    const removeListener = moduleBuilder.commit((state, { assignmentId, userId, listenerId, }) => {
        const byAssignment = state.subscriptions?.[userId]?.[assignmentId];
        if (byAssignment == null) {
            return;
        }
        byAssignment.listeners.delete(listenerId);
        byAssignment.subscription.then(subscription => {
            if (byAssignment.listeners.size === 0) {
                subscription.unsubscribe();
            }
        });
    }, 'removeListener');
    /**
     * Add a single message to the store.
     *
     * @param state The current state of the store.
     * @param data The ids to point to a conversation.
     * @param message The actual message to add.
     */
    function addSingleMessage(state, data, message) {
        const msgState = getMsgState(state, data) ?? {
            pagination: { tag: 'not-started' },
        };
        commitMessages({
            ...data,
            newState: {
                ...msgState,
                data: [message].concat(msgState.data ?? []),
            },
        });
    }
    /** Perform the request to create a new message and commit it to the store. */
    AssistantsStore.createMessage = moduleBuilder.dispatch(async ({ state }, data) => {
        const { CreateAssistantMessageCommand } = await import('@codegrade/apiv2-client');
        const { message } = await apiv2Client.send(new CreateAssistantMessageCommand(data));
        addSingleMessage(state, data, message);
    }, 'createMessage');
    /**
     * Load a single message from the backend by its id.
     *
     * This is used when we are missing messages in the middle of the conversation.
     */
    AssistantsStore.loadSingleMessage = moduleBuilder.dispatch(async ({ state }, data) => {
        await AssistantsStore.loadMessages({
            assistantId: data.assistantId,
            conversationId: data.conversationId,
            toLoad: 'first-page',
        });
        const existing = state.messages[data.assistantId]?.[data.conversationId]?.data ?? [];
        if (existing.some(msg => msg.messageId === data.messageId)) {
            return;
        }
        const { GetAssistantMessageCommand } = await import('@codegrade/apiv2-client');
        const { message } = await apiv2Client.send(new GetAssistantMessageCommand(data));
        addSingleMessage(state, data, message);
    }, 'loadSingleMessage');
    /**
     * Handle a converation update from the websocket.
     *
     * @param state The current state of the store.
     * @param ids The ids to point to a conversation.
     * @param conversation The updated conversation.
     */
    async function handleConverationUpdate(state, ids, conversation) {
        const oldState = getConvoState(state, ids);
        const newState = {
            pagination: { tag: 'not-started' },
            ...oldState,
            data: [conversation].concat(oldState?.data ?? []),
        };
        commitConversations({
            ...ids,
            newState,
        });
    }
    /**
     * Create a function to perform handling of websocket updates.
     *
     * @param state The current state of the store.
     * @param ids The identifiers of the subscription.
     * @returns The function to handle websocket updates.
     */
    function getHandleWebsocketUpdate(state, ids) {
        return (update) => {
            const assistantId = update.assistantId;
            const conversationId = update.conversationId;
            const convoIds = {
                assistantId,
                conversationId,
            };
            expr.match(update, 'type', {
                conversation: async (convo) => {
                    handleConverationUpdate(state, ids, convo);
                },
                message: ({ message }) => {
                    expr.match(message, 'type', {
                        'user-message': () => {
                            AssistantsStore.loadSingleMessage({
                                ...convoIds,
                                messageId: message.messageId.toString(10),
                            });
                        },
                        'generation-started': startMsg => {
                            let aggregator = state.aggregators[assistantId]?.[conversationId]?.[message.messageId];
                            if (aggregator == null) {
                                aggregator = MessageAggregator.create(assistantId, conversationId, startMsg);
                            }
                            commitMessageAggregator({
                                ...convoIds,
                                aggregator: aggregator.processStart(startMsg.createdAt),
                            });
                        },
                        'output-chunk': outputMsg => {
                            let aggregator = state.aggregators[assistantId]?.[conversationId]?.[message.messageId];
                            if (aggregator == null) {
                                aggregator = MessageAggregator.create(assistantId, conversationId, outputMsg);
                            }
                            commitMessageAggregator({
                                ...convoIds,
                                aggregator: aggregator.processChunk(outputMsg),
                            });
                        },
                        'generation-error': error => {
                            // eslint-disable-next-line no-console
                            console.log('Error occurred during generation', error.error);
                        },
                        'generation-ended': up => {
                            const aggregator = Maybe.fromNullable(state.aggregators[assistantId]?.[conversationId]?.[message.messageId]);
                            aggregator
                                .chain(agg => agg.finalize(up))
                                .caseOf({
                                Just: finalized => addSingleMessage(state, convoIds, finalized),
                                Nothing: () => {
                                    AssistantsStore.loadSingleMessage({
                                        ...convoIds,
                                        messageId: message.messageId.toString(10),
                                    });
                                },
                            });
                            removeMessageAggregator({
                                ...convoIds,
                                messageId: message.messageId,
                            });
                        },
                    });
                },
            });
        };
    }
    /**
     * Signal that you depend on an assistant conversation subscription.
     *
     * This will load the subscription if it doesn't exist yet, or will add a listener if it does
     * already exist.
     *
     * @returns A promise that will resolve directly to a `LoadResult`. You should call its drop
     * function when you don't depend on the subscription anymore.
     */
    AssistantsStore.addDependency = moduleBuilder.dispatch(async ({ state }, ids) => {
        const listenerId = newListenerId();
        let data = state.subscriptions[ids.userId]?.[ids.assignmentId];
        if (data == null) {
            const subscription = eitherAsync.toPromise(getWsClient().chain(wsClient => wsClient.subscribeTo.assistantConversations({
                assignmentId: ids.assignmentId,
                conversationOwnerId: ids.userId,
            }, 
            // On update
            getHandleWebsocketUpdate(state, ids), 
            // On error just remove the subscription for now, TODO: notify about
            // error.
            () => {
                removeSubscription(ids);
            })));
            subscription.catch(() => {
                removeSubscription(ids);
            });
            data = {
                subscription,
                listeners: new Set(),
            };
        }
        addSubscription({
            ...ids,
            data: {
                ...data,
                listeners: new Set(utils.itertools.chain([data.listeners, [listenerId]])),
            },
        });
        await data.subscription;
        return {
            drop: () => removeListener({ ...ids, listenerId }),
        };
    }, 'addDependency');
    utils.signal.AFTER_USER_CHANGED.register(async () => {
        commitClearAll();
    });
})(AssistantsStore || (AssistantsStore = {}));
